import { ChangeEvent, FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import {
  FormFieldsBox,
  NarrowBox,
  StringUtils,
} from '@spotify/confidence-core-react';
import { FormSubmitButtons } from '@spotify/confidence-core-react';

import { Branding } from './CreateAccountForm';
import { IntroStep } from './IntroStep';
import { useHubspotForm } from './hooks/useHubspotForm';
import final from './img/final.png';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: theme.palette.background.default,
    zIndex: theme.zIndex.modal,
    overflowY: 'auto',
  },
}));

export const UnavailabilityPage = ({ onClose }: { onClose: () => void }) => {
  const classes = useStyles();
  const [{ loading, error, value }, postData] = useHubspotForm({
    portalId: '21894833',
    formId: 'e3cb3f9a-d0cd-4460-836c-a72e1e9435f3',
  });
  const [searchParams] = useSearchParams();
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [acceptTerms, setAcceptTerms] = useState<boolean>(false);

  const country = searchParams.get('country') ?? 'unknown';

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    postData([
      {
        objectTypeId: '0-1',
        name: 'email',
        value: email,
      },
      {
        name: 'company',
        value: company,
      },
      {
        name: 'country',
        value: country,
      },
    ]);
  };
  const disabled =
    company === '' ||
    !StringUtils.isValidEmail(email) ||
    loading ||
    !acceptTerms;

  return (
    <Box className={classes.container}>
      <Branding />
      {value ? (
        <IntroStep
          title="Thank you for your interest in Confidence! We'll reach out once we launch in your country."
          img={final}
        />
      ) : (
        <Box paddingTop="15vh">
          <NarrowBox width="sm">
            <Typography variant="h4">
              Enter details for notification{' '}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              We will notify you when we are available in your country.
            </Typography>
            <Box marginTop={1}>
              {error && <Alert severity="error">{error.message}</Alert>}
              {!value && (
                <form onSubmit={submit}>
                  <FormFieldsBox>
                    <TextField
                      type="email"
                      inputProps={{
                        inputMode: 'email',
                        autoCapitalize: 'never',
                      }}
                      value={email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setEmail(e.target.value)
                      }
                      variant="outlined"
                      margin="dense"
                      label="E-mail"
                    />

                    <TextField
                      value={company}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setCompany(e.target.value)
                      }
                      variant="outlined"
                      margin="dense"
                      label="Company"
                    />
                    <div>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={acceptTerms}
                            onChange={(_e, checked) => setAcceptTerms(checked)}
                            name="terms-of-service"
                          />
                        }
                        label="I agree to receive communications from Confidence."
                      />
                      <FormHelperText>
                        You may unsubscribe from these communications at any
                        time. Please review our{' '}
                        <Link
                          underline="always"
                          target="_blank"
                          href="https://www.spotify.com/us/legal/privacy-policy/"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </FormHelperText>
                    </div>
                  </FormFieldsBox>
                  <Box textAlign="right" pt={2}>
                    <FormSubmitButtons
                      onCancel={onClose}
                      disabled={disabled}
                      loading={loading}
                      cancelLabel="Close"
                      label="Submit"
                    />
                  </Box>
                </form>
              )}
            </Box>
          </NarrowBox>
        </Box>
      )}
    </Box>
  );
};
